<template>
  <div class="notification-center">
    <!-- Header -->
    <b-card>
      <b-row class="align-items-center">
        <b-col
          cols="12"
          md="6"
        >
          <h2 class="mb-0">
            <feather-icon
              icon="BellIcon"
              size="24"
              class="ml-50"
            />
            مركز الإشعارات
          </h2>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="text-left mt-1 mt-md-0"
        >
          <b-button
            v-if="unreadCount > 0"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            size="sm"
            class="ml-1"
            @click="handleMarkAllAsRead"
          >
            <feather-icon
              icon="CheckIcon"
              size="14"
              class="ml-25"
            />
            تحديد الكل كمقروء ({{ unreadCount }})
          </b-button>
          <b-button
            v-if="items.length > 0"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-danger"
            size="sm"
            @click="handleClearAll"
          >
            <feather-icon
              icon="Trash2Icon"
              size="14"
              class="ml-25"
            />
            مسح الكل
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Statistics Cards -->
    <b-row class="match-height">
      <b-col
        lg="3"
        sm="6"
      >
        <statistic-card-horizontal
          icon="BellIcon"
          color="primary"
          :statistic="stats.total.toString()"
          statistic-title="إجمالي الإشعارات"
        />
      </b-col>
      <b-col
        lg="3"
        sm="6"
      >
        <statistic-card-horizontal
          icon="MailIcon"
          color="warning"
          :statistic="stats.unread.toString()"
          statistic-title="غير مقروء"
        />
      </b-col>
      <b-col
        lg="3"
        sm="6"
      >
        <statistic-card-horizontal
          icon="CheckCircleIcon"
          color="success"
          :statistic="stats.read.toString()"
          statistic-title="مقروء"
        />
      </b-col>
      <b-col
        lg="3"
        sm="6"
      >
        <statistic-card-horizontal
          icon="AlertTriangleIcon"
          color="danger"
          :statistic="((stats.by_priority && stats.by_priority.urgent) || 0).toString()"
          statistic-title="عاجل"
        />
      </b-col>
    </b-row>

    <!-- Filters and Search -->
    <b-card>
      <b-row>
        <!-- Search -->
        <b-col
          cols="12"
          md="4"
          class="mb-1 mb-md-0"
        >
          <b-form-group>
            <b-input-group>
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="searchQuery"
                placeholder="البحث في الإشعارات..."
                @input="debouncedSearch"
              />
            </b-input-group>
          </b-form-group>
        </b-col>

        <!-- Status Filter -->
        <b-col
          cols="12"
          md="2"
          class="mb-1 mb-md-0"
        >
          <b-form-group>
            <v-select
              v-model="localFilters.status"
              :options="statusOptions"
              :reduce="reduceToValue"
              label="text"
              placeholder="الحالة"
              @input="handleFilterChange"
            />
          </b-form-group>
        </b-col>

        <!-- Priority Filter -->
        <b-col
          cols="12"
          md="2"
          class="mb-1 mb-md-0"
        >
          <b-form-group>
            <v-select
              v-model="localFilters.priority"
              :options="priorityOptions"
              :reduce="reduceToValue"
              label="text"
              placeholder="الأولوية"
              @input="handleFilterChange"
            />
          </b-form-group>
        </b-col>

        <!-- Type Filter -->
        <b-col
          cols="12"
          md="2"
          class="mb-1 mb-md-0"
        >
          <b-form-group>
            <v-select
              v-model="localFilters.type"
              :options="typeOptions"
              :reduce="reduceToValue"
              label="text"
              placeholder="النوع"
              @input="handleFilterChange"
            />
          </b-form-group>
        </b-col>

        <!-- Sort -->
        <b-col
          cols="12"
          md="2"
        >
          <b-form-group>
            <v-select
              v-model="sortOption"
              :options="sortOptions"
              :reduce="reduceToValue"
              label="text"
              @input="handleSortChange"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Active Filters Tags -->
      <b-row
        v-if="hasActiveFilters"
        class="mt-1"
      >
        <b-col cols="12">
          <div class="d-flex align-items-center flex-wrap">
            <span class="text-muted ml-50">الفلاتر النشطة:</span>
            <b-badge
              v-if="localFilters.status !== 'all'"
              variant="light-primary"
              class="ml-50"
              pill
            >
              {{ getStatusText(localFilters.status) }}
              <feather-icon
                icon="XIcon"
                size="12"
                class="cursor-pointer mr-25"
                @click="clearFilter('status')"
              />
            </b-badge>
            <b-badge
              v-if="localFilters.priority !== 'all'"
              variant="light-warning"
              class="ml-50"
              pill
            >
              {{ getPriorityText(localFilters.priority) }}
              <feather-icon
                icon="XIcon"
                size="12"
                class="cursor-pointer mr-25"
                @click="clearFilter('priority')"
              />
            </b-badge>
            <b-badge
              v-if="localFilters.type !== 'all'"
              variant="light-info"
              class="ml-50"
              pill
            >
              {{ localFilters.type }}
              <feather-icon
                icon="XIcon"
                size="12"
                class="cursor-pointer mr-25"
                @click="clearFilter('type')"
              />
            </b-badge>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="py-25 px-50 ml-50"
              @click="clearAllFilters"
            >
              مسح الكل
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Notifications List -->
    <b-card no-body>
      <!-- Loading State -->
      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner
          variant="primary"
          label="جاري التحميل..."
        />
        <p class="mt-1 text-muted">
          جاري تحميل الإشعارات...
        </p>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="items.length === 0"
        class="empty-state text-center py-5"
      >
        <feather-icon
          icon="BellOffIcon"
          size="64"
          class="text-muted mb-2"
        />
        <h4 class="text-muted">
          لا توجد إشعارات
        </h4>
        <p class="text-muted">
          لم يتم العثور على أي إشعارات تطابق معايير البحث
        </p>
      </div>

      <!-- Notifications List -->
      <b-list-group
        v-else
        flush
      >
        <b-list-group-item
          v-for="notification in items"
          :key="notification.id"
          :class="[
            'notification-list-item',
            { 'unread-notification': !notification.is_read }
          ]"
          @click="handleNotificationClick(notification)"
        >
          <b-media no-body>
            <b-media-aside>
              <b-avatar
                size="48"
                :variant="getPriorityVariant(notification.priority)"
              >
                <feather-icon
                  :icon="getNotificationIcon(notification.type)"
                  size="20"
                />
              </b-avatar>
            </b-media-aside>

            <b-media-body>
              <div class="d-flex justify-content-between align-items-start">
                <div class="notification-content">
                  <h5 class="mb-25">
                    {{ notification.title }}
                    <b-badge
                      :variant="getPriorityBadgeVariant(notification.priority)"
                      class="mr-50"
                      pill
                    >
                      {{ getPriorityText(notification.priority) }}
                    </b-badge>
                  </h5>
                  <p class="mb-50">
                    {{ notification.message }}
                  </p>
                  <div class="d-flex align-items-center">
                    <small class="text-muted">
                      <feather-icon
                        icon="ClockIcon"
                        size="12"
                        class="ml-25"
                      />
                      {{ notification.time_ago || formatTime(notification.created_at) }}
                    </small>
                    <b-badge
                      v-if="notification.type_display_name"
                      variant="light-info"
                      class="mr-50"
                    >
                      {{ notification.type_display_name }}
                    </b-badge>
                  </div>
                </div>

                <div class="notification-actions">
                  <b-button
                    v-if="!notification.is_read"
                    v-b-tooltip.hover
                    title="تحديد كمقروء"
                    variant="flat-success"
                    size="sm"
                    class="btn-icon"
                    @click.stop="handleMarkAsRead(notification.id)"
                  >
                    <feather-icon
                      icon="CheckIcon"
                      size="16"
                    />
                  </b-button>
                  <b-button
                    v-b-tooltip.hover
                    title="حذف"
                    variant="flat-danger"
                    size="sm"
                    class="btn-icon mr-25"
                    @click.stop="handleDelete(notification.id)"
                  >
                    <feather-icon
                      icon="Trash2Icon"
                      size="16"
                    />
                  </b-button>
                </div>
              </div>

              <!-- Unread indicator -->
              <div
                v-if="!notification.is_read"
                class="unread-indicator"
              />
            </b-media-body>
          </b-media>
        </b-list-group-item>
      </b-list-group>

      <!-- Pagination -->
      <b-card-footer v-if="pagination.last_page > 1">
        <b-pagination
          v-model="currentPage"
          :total-rows="pagination.total"
          :per-page="pagination.per_page"
          align="center"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          @change="handlePageChange"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </b-card-footer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BListGroup,
  BListGroupItem,
  BMedia,
  BMediaAside,
  BMediaBody,
  BAvatar,
  BBadge,
  BSpinner,
  BPagination,
  BCardFooter,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { mapGetters, mapActions } from 'vuex'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Swal from 'sweetalert2'
import { handleGisImportNotification } from '@/utils/egyptGis'

export default {
  name: 'NotificationCenter',

  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BListGroup,
    BListGroupItem,
    BMedia,
    BMediaAside,
    BMediaBody,
    BAvatar,
    BBadge,
    BSpinner,
    BPagination,
    BCardFooter,
    vSelect,
    StatisticCardHorizontal,
  },

  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },

  data() {
    return {
      searchQuery: '',
      currentPage: 1,
      localFilters: {
        status: 'all',
        priority: 'all',
        type: 'all',
      },
      sortOption: 'newest',

      statusOptions: [
        { value: 'all', text: 'جميع الحالات' },
        { value: 'unread', text: 'غير مقروء' },
        { value: 'read', text: 'مقروء' },
      ],

      priorityOptions: [
        { value: 'all', text: 'جميع الأولويات' },
        { value: 'urgent', text: 'عاجل' },
        { value: 'high', text: 'عالي' },
        { value: 'normal', text: 'عادي' },
        { value: 'low', text: 'منخفض' },
      ],

      typeOptions: [
        { value: 'all', text: 'جميع الأنواع' },
        { value: 'task_assigned', text: 'مهمة مُسندة' },
        { value: 'project_updated', text: 'تحديث مشروع' },
        { value: 'payment_received', text: 'دفعة مستلمة' },
        { value: 'system_alert', text: 'تنبيه نظام' },
        { value: 'message_received', text: 'رسالة مستلمة' },
      ],

      sortOptions: [
        { value: 'newest', text: 'الأحدث أولاً' },
        { value: 'oldest', text: 'الأقدم أولاً' },
        { value: 'priority', text: 'حسب الأولوية' },
      ],
    }
  },

  computed: {
    ...mapGetters('notifications', [
      'items',
      'unreadCount',
      'pagination',
      'stats',
      'loading',
    ]),

    hasActiveFilters() {
      return (
        this.localFilters.status !== 'all'
        || this.localFilters.priority !== 'all'
        || this.localFilters.type !== 'all'
        || this.searchQuery !== ''
      )
    },
  },

  watch: {
    currentPage(newPage) {
      this.loadNotifications()
    },
  },

  async created() {
    await this.initialize()
    // Create debounced search function
    let timeout
    this.debouncedSearch = () => {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        this.handleSearch()
      }, 500)
    }
  },

  methods: {
    ...mapActions('notifications', [
      'fetchNotifications',
      'fetchUnreadCount',
      'fetchUnreadCount_168',
      'fetchStats',
      'markAsRead',
      'markAllAsRead',
      'deleteNotification',
      'clearAll',
      'updateFilters',
    ]),

    /**
     * Reduce function for v-select
     */
    reduceToValue(option) {
      return option.value
    },

    /**
     * Initialize component
     */
    async initialize() {
      try {
        await Promise.all([
          this.loadNotifications(),
          this.fetchStats(),
        ])
      } catch (error) {
        console.error('Error initializing notification center:', error)
      }
    },

    /**
     * Load notifications
     */
    async loadNotifications() {
      const params = {
        page: this.currentPage,
        per_page: 15,
        search: this.searchQuery,
        ...this.getSortParams(),
      }

      if (this.localFilters.status !== 'all') {
        params.status = this.localFilters.status
      }
      if (this.localFilters.priority !== 'all') {
        params.priority = this.localFilters.priority
      }
      if (this.localFilters.type !== 'all') {
        params.type = this.localFilters.type
      }

      try {
        await this.fetchNotifications(params)
      } catch (error) {
        this.showErrorToast('حدث خطأ أثناء تحميل الإشعارات')
      }
    },

    /**
     * Get sort parameters
     */
    getSortParams() {
      const sortMap = {
        newest: { sort_by: 'created_at', sort_order: 'desc' },
        oldest: { sort_by: 'created_at', sort_order: 'asc' },
        priority: { sort_by: 'priority', sort_order: 'desc' },
      }
      return sortMap[this.sortOption] || sortMap.newest
    },

    /**
     * Handle filter change
     */
    handleFilterChange() {
      this.currentPage = 1
      this.loadNotifications()
    },

    /**
     * Handle sort change
     */
    handleSortChange() {
      this.currentPage = 1
      this.loadNotifications()
    },

    /**
     * Handle search
     */
    handleSearch() {
      this.currentPage = 1
      this.loadNotifications()
    },

    /**
     * Handle page change
     */
    handlePageChange(page) {
      this.currentPage = page
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    /**
     * Clear specific filter
     */
    clearFilter(filterName) {
      this.localFilters[filterName] = 'all'
      this.handleFilterChange()
    },

    /**
     * Clear all filters
     */
    clearAllFilters() {
      this.localFilters = {
        status: 'all',
        priority: 'all',
        type: 'all',
      }
      this.searchQuery = ''
      this.handleFilterChange()
    },

    /**
     * Handle notification click
     */
    async handleNotificationClick(notification) {
      if (!notification.is_read) {
        try {
          await this.markAsRead(notification.id)
        } catch (error) {
          console.error('Error marking as read:', error)
        }
      }

      if (handleGisImportNotification(notification)) return

      if (notification.notifiable) {
        this.navigateToEntity(notification.notifiable)
      }
    },

    /**
     * Handle mark as read
     */
    async handleMarkAsRead(notificationId) {
      try {
        await this.markAsRead(notificationId)
        this.showSuccessToast('تم تحديد الإشعار كمقروء')
      } catch (error) {
        this.showErrorToast('حدث خطأ أثناء تحديث الإشعار')
      }
    },

    /**
     * Handle mark all as read
     */
    async handleMarkAllAsRead() {
      if (this.unreadCount === 0) return

      try {
        await this.markAllAsRead()
        await this.fetchStats()
        this.showSuccessToast(`تم تحديد ${this.unreadCount} إشعار كمقروء`)
      } catch (error) {
        this.showErrorToast('حدث خطأ أثناء تحديث الإشعارات')
      }
    },

    /**
     * Handle delete
     */
    async handleDelete(notificationId) {
      const result = await Swal.fire({
        title: 'هل أنت متأكد؟',
        text: 'سيتم حذف هذا الإشعار',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'نعم، احذف',
        cancelButtonText: 'إلغاء',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })

      if (result.isConfirmed) {
        try {
          await this.deleteNotification(notificationId)
          await this.fetchStats()
          this.showSuccessToast('تم حذف الإشعار بنجاح')
        } catch (error) {
          this.showErrorToast('حدث خطأ أثناء حذف الإشعار')
        }
      }
    },

    /**
     * Handle clear all
     */
    async handleClearAll() {
      const result = await Swal.fire({
        title: 'هل أنت متأكد؟',
        text: 'سيتم حذف جميع الإشعارات ولا يمكن التراجع عن هذا الإجراء',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'نعم، احذف الكل',
        cancelButtonText: 'إلغاء',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })

      if (result.isConfirmed) {
        try {
          await this.clearAll()
          await this.fetchStats()
          this.showSuccessToast('تم حذف جميع الإشعارات')
        } catch (error) {
          this.showErrorToast('حدث خطأ أثناء حذف الإشعارات')
        }
      }
    },

    /**
     * Navigate to entity
     */
    navigateToEntity(notifiable) {
      const routes = {
        Task: { name: 'apps-todo-task', params: { id: notifiable.id } },
        Project: { name: 'projects-view', params: { id: notifiable.id } },
        User: { name: 'users-view', params: { id: notifiable.id } },
        Department: { name: 'departments-view', params: { id: notifiable.id } },
        Request: { name: 'requests-view', params: { id: notifiable.id } },
      }

      const route = routes[notifiable.type]
      if (route) {
        this.$router.push(route).catch(() => {})
      }
    },

    /**
     * Get notification icon
     */
    getNotificationIcon(type) {
      const icons = {
        task_assigned: 'CheckSquareIcon',
        project_updated: 'BriefcaseIcon',
        payment_received: 'DollarSignIcon',
        system_alert: 'AlertTriangleIcon',
        message_received: 'MailIcon',
        deadline_reminder: 'ClockIcon',
        user_mention: 'AtSignIcon',
        status_changed: 'RefreshCwIcon',
        file_uploaded: 'UploadIcon',
        comment_added: 'MessageSquareIcon',
        gis_import_completed: 'CheckCircleIcon',
        gis_import_failed: 'AlertCircleIcon',
      }
      return icons[type] || 'BellIcon'
    },

    /**
     * Get priority variant
     */
    getPriorityVariant(priority) {
      const variants = {
        low: 'light-secondary',
        normal: 'light-primary',
        high: 'light-warning',
        urgent: 'light-danger',
      }
      return variants[priority] || 'light-primary'
    },

    /**
     * Get priority badge variant
     */
    getPriorityBadgeVariant(priority) {
      const variants = {
        low: 'light-secondary',
        normal: 'light-info',
        high: 'light-warning',
        urgent: 'light-danger',
      }
      return variants[priority] || 'light-info'
    },

    /**
     * Get priority text
     */
    getPriorityText(priority) {
      const texts = {
        low: 'منخفض',
        normal: 'عادي',
        high: 'عالي',
        urgent: 'عاجل',
      }
      return texts[priority] || 'عادي'
    },

    /**
     * Get status text
     */
    getStatusText(status) {
      const texts = {
        unread: 'غير مقروء',
        read: 'مقروء',
      }
      return texts[status] || status
    },

    /**
     * Format time
     */
    formatTime(timestamp) {
      if (!timestamp) return ''

      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'الآن'
      if (diffMins < 60) return `منذ ${diffMins} دقيقة`
      if (diffHours < 24) return `منذ ${diffHours} ساعة`
      if (diffDays < 7) return `منذ ${diffDays} يوم`

      return date.toLocaleDateString('ar-EG')
    },

    /**
     * Show success toast
     */
    showSuccessToast(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'نجح',
          icon: 'CheckIcon',
          text: message,
          variant: 'success',
        },
      })
    },

    /**
     * Show error toast
     */
    showErrorToast(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'خطأ',
          icon: 'XIcon',
          text: message,
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.notification-center {
  .notification-list-item {
    cursor: pointer;
    transition: all 0.2s ease;
    border-right: 3px solid transparent;

    &:hover {
      background-color: rgba(115, 103, 240, 0.04);

      .notification-actions {
        opacity: 1;
      }
    }

    &.unread-notification {
      background-color: rgba(115, 103, 240, 0.08);
      border-right-color: #7367f0;

      &:hover {
        background-color: rgba(115, 103, 240, 0.12);
      }
    }
  }

  .notification-content {
    flex: 1;
  }

  .notification-actions {
    display: flex;
    gap: 0.5rem;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .unread-indicator {
    position: absolute;
    top: 50%;
    left: 1rem;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background-color: #7367f0;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  .empty-state {
    padding: 3rem 1rem;
  }
}

// Dark mode support
.dark-layout {
  .notification-list-item {
    &:hover {
      background-color: rgba(115, 103, 240, 0.08);
    }

    &.unread-notification {
      background-color: rgba(115, 103, 240, 0.12);

      &:hover {
        background-color: rgba(115, 103, 240, 0.16);
      }
    }
  }
}
</style>
